import { useEffect, useMemo, useState } from 'react';
import { Badge, Box, Button, Center, Flex, Grid, Input, Spinner, Text } from '@chakra-ui/react';
import api from '@/utils/api';
import { unwrap } from '@/pages/courses/api';
import { ProgressBar } from '@/components/ui/progress-bar';
import { PillGroup } from '@/components/ui/pill-group';
import { Course, CourseLearnerMetrics, CourseMetrics } from '@/lib/types';

type LearnerStatus = 'complete' | 'overdue' | 'behind' | 'in_progress' | 'not_started';

type StatusFilter = 'all' | LearnerStatus;

const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'complete', label: 'Complete' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'not_started', label: 'Not started' },
  { value: 'behind', label: 'Behind' },
  { value: 'overdue', label: 'Overdue' },
];

// One label per learner, most urgent first: a finished learner is never
// behind or overdue, an unfinished one past the due date reads overdue even
// when also behind the weekly pace.
function statusOf(learner: CourseLearnerMetrics): LearnerStatus {
  if (learner.completed_at) return 'complete';
  if (learner.is_overdue) return 'overdue';
  if (learner.is_behind) return 'behind';
  if (learner.started_at) return 'in_progress';
  return 'not_started';
}

function statusBadge(learner: CourseLearnerMetrics) {
  const status = statusOf(learner);
  if (status === 'complete') {
    return (
      <Text fontSize='sm' color='success' fontWeight='600'>
        {new Date(learner.completed_at as string).toLocaleDateString()}
      </Text>
    );
  }
  const styles: Record<Exclude<LearnerStatus, 'complete'>, { bg: string; color: string; label: string }> = {
    overdue: { bg: 'danger.subtle', color: 'danger', label: 'Overdue' },
    behind: { bg: 'danger.subtle', color: 'danger', label: 'Behind' },
    in_progress: { bg: 'brand.soft', color: 'brand.fg', label: 'In progress' },
    not_started: { bg: 'bg.subtle', color: 'fg.muted', label: 'Not started' },
  };
  const style = styles[status];
  return (
    <Badge bg={style.bg} color={style.color} fontSize='2xs' textTransform='uppercase' w='fit-content'>
      {style.label}
    </Badge>
  );
}

// Escapes one CSV cell: quotes doubled, wrapped when the value needs it.
function csvCell(value: string | number): string {
  const text = String(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

// Admin completion metrics. Individually tracked courses show the
// who-completed-what-when roster with per-module dots; count-only courses
// show anonymous aggregate counters — numbers, never names.
export default function TrackingTab({ course }: { course: Course }) {
  const [metrics, setMetrics] = useState<CourseMetrics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    api
      .get(`courses/${course.id}/metrics`)
      .then((res) => setMetrics(unwrap<CourseMetrics>(res)))
      .catch(() => setMetrics(null))
      .finally(() => setIsLoading(false));
  }, [course.id]);

  const visibleLearners = useMemo(() => {
    let learners = metrics?.learners ?? [];
    if (statusFilter !== 'all') {
      learners = learners.filter((learner) => statusOf(learner) === statusFilter);
    }
    const query = searchQuery.trim().toLowerCase();
    if (query) {
      learners = learners.filter((learner) =>
        `${learner.first_name} ${learner.last_name} ${learner.email}`.toLowerCase().includes(query),
      );
    }
    return learners;
  }, [metrics, statusFilter, searchQuery]);

  if (isLoading) {
    return (
      <Center py={10}>
        <Spinner size='lg' />
      </Center>
    );
  }

  if (!metrics) {
    return (
      <Text fontSize='sm' color='fg.muted' textAlign='center' py={8}>
        Failed to load completion metrics.
      </Text>
    );
  }

  const statCard = (label: string, value: string | number, hint?: string, color?: string) => (
    <Box bg='bg.panel' borderWidth='1px' borderColor='border' borderRadius='lg' shadow='sm' p={5}>
      <Text
        fontFamily='heading'
        fontSize='2xs'
        fontWeight='700'
        textTransform='uppercase'
        letterSpacing='0.16em'
        color='fg.muted'
        mb={2}
      >
        {label}
      </Text>
      <Text fontFamily='heading' fontSize='3xl' fontWeight='800' color={color ?? 'fg.strong'}>
        {value}
      </Text>
      {hint && (
        <Text fontSize='xs' color='fg.muted' mt={1}>
          {hint}
        </Text>
      )}
    </Box>
  );

  if (metrics.tracking_mode === 'count_only') {
    return (
      <Box>
        <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={4} maxW='2xl'>
          {statCard('Started', metrics.started_count, 'First item completed on the shared link')}
          {statCard('Completed', metrics.completed_count, `All ${metrics.item_count} items finished`)}
        </Grid>
        <Text fontSize='xs' color='fg.muted' mt={4} maxW='xl'>
          This course counts completions anonymously — the same person on two devices counts
          twice. For a per-person roster, build the next course as individually tracked.
        </Text>
      </Box>
    );
  }

  const pct =
    metrics.assigned_count > 0 ? (metrics.completed_count / metrics.assigned_count) * 100 : 0;
  const hasPaceColumn = metrics.behind_count > 0 || metrics.overdue_count > 0;

  const exportCsv = () => {
    const header = [
      'Name',
      'Email',
      'Role',
      'Items completed',
      'Items total',
      'Version',
      'Status',
      'Started',
      'Completed',
    ];
    const lines = (metrics.learners ?? []).map((learner) =>
      [
        `${learner.first_name} ${learner.last_name}`.trim(),
        learner.email,
        learner.role,
        learner.completed_item_count,
        metrics.item_count,
        learner.version > 0 ? `v${learner.version}` : '',
        statusOf(learner).replace('_', ' '),
        learner.started_at ? new Date(learner.started_at).toISOString() : '',
        learner.completed_at ? new Date(learner.completed_at).toISOString() : '',
      ]
        .map(csvCell)
        .join(','),
    );
    const blob = new Blob([[header.join(','), ...lines].join('\n')], {
      type: 'text/csv;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${course.name.replace(/[^\w-]+/g, '-').toLowerCase()}-tracking.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box>
      <Grid
        templateColumns={{ base: '1fr 1fr', md: `repeat(${hasPaceColumn ? 4 : 3}, 1fr)` }}
        gap={4}
        mb={5}
      >
        {statCard('Assigned', metrics.assigned_count)}
        {statCard('In progress', Math.max(0, metrics.started_count - metrics.completed_count))}
        {statCard('Completed', metrics.completed_count)}
        {hasPaceColumn &&
          statCard(
            metrics.overdue_count > 0 ? 'Overdue' : 'Behind pace',
            metrics.overdue_count > 0 ? metrics.overdue_count : metrics.behind_count,
            metrics.overdue_count > 0 ? 'Past the course due date' : 'Behind the reminder pace',
            'danger',
          )}
      </Grid>

      <ProgressBar
        value={pct}
        label={`${metrics.completed_count} of ${metrics.assigned_count} assigned complete`}
        valueLabel={`${Math.round(pct)}%`}
        thickness='8px'
        mb={6}
        maxW='2xl'
      />

      {(metrics.modules ?? []).length > 0 && metrics.assigned_count > 0 && (
        <Box
          bg='bg.panel'
          borderWidth='1px'
          borderColor='border'
          borderRadius='lg'
          shadow='sm'
          p={5}
          mb={6}
        >
          <Text
            fontFamily='heading'
            fontSize='2xs'
            fontWeight='700'
            textTransform='uppercase'
            letterSpacing='0.16em'
            color='fg.muted'
            mb={3}
          >
            Completion by module
          </Text>
          <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gapX={8} gapY={3}>
            {(metrics.modules ?? []).map((module) => (
              <ProgressBar
                key={module.module_id}
                value={
                  metrics.assigned_count > 0
                    ? (module.completed_count / metrics.assigned_count) * 100
                    : 0
                }
                label={module.name || 'Untitled module'}
                valueLabel={`${module.completed_count} of ${metrics.assigned_count}`}
              />
            ))}
          </Grid>
        </Box>
      )}

      <Box
        bg='bg.panel'
        borderWidth='1px'
        borderColor='border'
        borderRadius='lg'
        shadow='sm'
        overflow='hidden'
      >
        <Flex
          align='center'
          gap={3}
          px={4}
          py={3}
          borderBottomWidth='1px'
          borderColor='border'
          wrap='wrap'
        >
          <Box flex='1' minW='180px' maxW='280px'>
            <Input
              size='sm'
              placeholder='Search people…'
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </Box>
          <PillGroup
            size='sm'
            items={STATUS_FILTERS}
            value={statusFilter}
            onChange={setStatusFilter}
          />
          <Button variant='outline' size='xs' ml='auto' onClick={exportCsv}>
            Export CSV
          </Button>
        </Flex>

        <Grid
          templateColumns='minmax(0,1.5fr) 0.8fr 1.3fr 0.6fr 0.9fr'
          gap={3}
          px={4}
          py={3}
          borderBottomWidth='1px'
          borderColor='border'
        >
          {['Person', 'Role', 'Modules', 'Version', 'Completed'].map((label) => (
            <Text
              key={label}
              fontFamily='heading'
              fontSize='2xs'
              fontWeight='600'
              textTransform='uppercase'
              letterSpacing='0.06em'
              color='fg.muted'
            >
              {label}
            </Text>
          ))}
        </Grid>

        {(metrics.learners ?? []).length === 0 && (
          <Text fontSize='sm' color='fg.muted' textAlign='center' py={6}>
            Nobody has been assigned yet.
          </Text>
        )}
        {(metrics.learners ?? []).length > 0 && visibleLearners.length === 0 && (
          <Text fontSize='sm' color='fg.muted' textAlign='center' py={6}>
            Nobody matches the current filters.
          </Text>
        )}

        {visibleLearners.map((learner) => {
          const name = `${learner.first_name} ${learner.last_name}`.trim() || learner.email;
          return (
            <Grid
              key={learner.user_id}
              templateColumns='minmax(0,1.5fr) 0.8fr 1.3fr 0.6fr 0.9fr'
              gap={3}
              px={4}
              py={3.5}
              borderBottomWidth='1px'
              borderColor='border'
              alignItems='center'
              _hover={{ bg: 'bg.subtle' }}
            >
              <Box minW={0}>
                <Text fontSize='sm' fontWeight='600' color='fg.strong' truncate>
                  {name}
                </Text>
                <Text fontSize='xs' color='fg.muted' truncate>
                  {learner.email}
                </Text>
              </Box>
              <Text fontSize='sm' color='fg.muted'>
                {learner.role || '—'}
              </Text>
              <Flex align='center' gap={1}>
                {learner.modules_completed.map((done, index) => (
                  <Box
                    // Dots are positional by construction (one per module in
                    // walk order), so the index IS the identity here.
                    key={`${learner.user_id}-${index}`}
                    w='12px'
                    h='6px'
                    borderRadius='2px'
                    bg={done ? 'success' : 'bg.subtle'}
                    borderWidth='1px'
                    borderColor={done ? 'success' : 'border.emphasized'}
                  />
                ))}
                <Text fontSize='xs' color='fg.muted' ml={1.5}>
                  {learner.completed_item_count}/{metrics.item_count}
                </Text>
              </Flex>
              <Text fontSize='sm' color='fg.muted'>
                {learner.version > 0 ? `v${learner.version}` : '—'}
              </Text>
              {statusBadge(learner)}
            </Grid>
          );
        })}
      </Box>
    </Box>
  );
}
